import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useActiveAdventures } from '../hooks';
import { AuthPageLayout, AuthForm } from '../components/layout';
import Button from '../components/ui/Button';
import ActiveAdventureCard from '../components/game/ActiveAdventureCard';
import { pageStyles } from '../config/theme';
import apiService from '../services/api';

/**
 * AdminHomePage - Original home page without auto-redirect to Nexus
 * Accessible at /admin for authenticated users who want to see the original home page
 */
const AdminHomePage: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  
  // Always call the hook, but it will gracefully handle non-authenticated users
  const { adventures: activeAdventures, loading: activeAdventuresLoading, error: activeAdventuresError, refetch: refetchActiveAdventures } = useActiveAdventures({ loadSceneDetails: true });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // No form submission needed for homepage
  };

  const handleCleanupAdventures = async () => {
    if (!user?.email) {
      console.error('No user email available for cleanup');
      return;
    }

    setIsCleaningUp(true);
    try {
      const result = await apiService.cleanupUserAdventures(user.email);
      console.log(`Cleaned up ${result.cleanedCount} adventures for ${result.userEmail}`);
      
      // Refresh the active adventures list
      await refetchActiveAdventures();
      
      // Show success message (you could add a toast notification here)
      alert(`Successfully cleaned up ${result.cleanedCount} adventures!`);
    } catch (error) {
      console.error('Failed to cleanup adventures:', error);
      alert('Failed to cleanup adventures. Please try again.');
    } finally {
      setIsCleaningUp(false);
    }
  };

  return (
    <AuthPageLayout
      title="CoopAdventureEngine"
      subtitle="Cooperative Adventure Game Engine (Admin)"
    >
      <AuthForm onSubmit={handleSubmit}>
        {isAuthenticated ? (
          // Authenticated user content
          <>
            <div className="mb-6">
              <p style={pageStyles.subtitle}>Welcome back, {user?.username}!</p>
              <p className="text-sm text-gray-400 mt-2">
                📍 Admin Home Page - Original home page without auto-redirect
              </p>
            </div>
            
            {/* Active Adventures Section */}
            {isAuthenticated && (
              <>
                {activeAdventuresLoading ? (
                  <div className="px-6 py-2 mb-6">
                    <p className="text-sm text-gray-500 text-center">Loading active adventures...</p>
                  </div>
                ) : activeAdventuresError ? (
                  <div className="px-6 py-2 mb-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-600 text-sm">
                        Failed to load active adventures: {activeAdventuresError}
                      </p>
                    </div>
                  </div>
                ) : activeAdventures.length > 0 ? (
                  <div className="px-6 py-2 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 
                        className="text-xl font-semibold"
                        style={{ color: '#ffffff' }}
                      >
                        🏃‍♂️ Active Adventures
                      </h2>
                      <Button
                        variant="glass-ghost"
                        size="sm"
                        onClick={handleCleanupAdventures}
                        disabled={isCleaningUp}
                      >
                        {isCleaningUp ? '🧹 Cleaning...' : '🧹 Close All'}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {activeAdventures.map((adventure) => (
                        <ActiveAdventureCard
                          key={adventure.session.id}
                          session={adventure.session}
                          characterName={adventure.characterName}
                          currentSceneDescription={adventure.currentSceneDescription}
                          playerId={adventure.playerId}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="px-6 py-2 mb-6">
                    <div
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '0.75rem',
                        padding: '1.5rem',
                        textAlign: 'center',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <p style={{ color: '#e0e7ff', marginBottom: '0.5rem' }}>📚 No active adventures</p>
                      <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Start a new adventure or join an existing one!</p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Main action buttons */}
            <div className="px-6 py-2">
              <div style={{ marginBottom: '32px' }}>
                <Link to="/adventures">
                  <Button 
                    variant="glass-primary" 
                    size="lg" 
                    fullWidth
                  >
                    ✨ Create Adventure
                  </Button>
                </Link>
              </div>
              
              <div style={{ marginBottom: '32px' }}>
                <Link to="/open-adventures">
                  <Button 
                    variant="glass-primary" 
                    size="lg" 
                    fullWidth
                  >
                    🎮 Join Adventure
                  </Button>
                </Link>
              </div>
              
              <div>
                <Button 
                  variant="glass-ghost" 
                  size="lg" 
                  fullWidth
                  onClick={logout}
                >
                  🚪 Sign Out
                </Button>
              </div>
            </div>
          </>
        ) : (
          // Unauthenticated user content
          <>
            <div className="mb-6">
              <p style={pageStyles.subtitle}>Sign in to start your adventure</p>
            </div>
            
            <div className="px-6 py-2">
              <div style={{ marginBottom: '32px' }}>
                <Link to="/auth/login">
                  <Button 
                    variant="glass-primary" 
                    size="lg" 
                    fullWidth
                  >
                    🔐 Sign In
                  </Button>
                </Link>
              </div>
              <div>
                <Link to="/auth/register">
                  <Button 
                    variant="glass-primary" 
                    size="lg" 
                    fullWidth
                  >
                    ✨ Create Account
                  </Button>
                </Link>
              </div>
            </div>
          </>
        )}
        
        <div className="mt-8 text-sm" style={pageStyles.subtitle}>
          <p>Play text-based adventures with friends</p>
          <p>Mobile-optimized • Real-time sync • PWA ready</p>
        </div>
        
        {/* Development Links */}
        <div className="mt-6 space-y-2">
          <Link 
            to="/ui-test" 
            style={pageStyles.link.primary}
            className="text-sm underline transition-colors duration-200 block"
          >
            🧪 UI Components Test Page
          </Link>
          <Link 
            to="/auth-test" 
            style={pageStyles.link.primary}
            className="text-sm underline transition-colors duration-200 block"
          >
            🔐 Authentication Test Page
          </Link>
        </div>
      </AuthForm>
    </AuthPageLayout>
  );
};

export default AdminHomePage;

